<template>
  <div>
    <div v-if="$apollo.loading">
      <loading-spinner class="my-5 text-center"/>
      <div class="text-center">Your Teams channels are being retrieved, this can take a few seconds.</div>
    </div>

    <template v-else>
      <!-- Main Form -->
      <b-row>
        <b-col cols="12">
          <h3
            class="pt-3"
          >
            <strong>Message Delivery</strong>
          </h3>
        </b-col>
        <b-col v-if="!isScheduled" cols="12">
          <b-alert show dismissible>Once you set your message status to 'Ready', you will only be able to update it until 5 minutes prior to its scheduled send time.</b-alert>
        </b-col>
        <b-col v-if="isScheduled" cols="12">
          <b-alert show>You cannot edit this message, as it is either delivered or already in progress.</b-alert>
        </b-col>
        <b-col cols="12">
          <advanced-form
            autocomplete="off"
            :hideFormControl="true"
            :form="formFields"
            v-bind="form"
            v-model="form"
          >

            <b-alert
              v-if="!Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length"
              slot="from-before"
              variant="warning"
              show
            >
              It seems like no accounts have been connected to Real Links yet. In order for us to send Microsoft Teams messages on your behalf, you need to go to <a :href="$router.resolve({ name: 'EmailSetup' }).href" target="_blank">Settings > Connect Teams</a> page.
            </b-alert>
          </advanced-form>
        </b-col>
      </b-row>

      <!-- Input Form with preview -->
      <b-row>
        <b-col cols="12">
          <h3
            class="pt-3"
          >
            <strong>Message Content</strong>
          </h3>
        </b-col>
        <b-col cols="12" lg="7">
          <div class="mb-3">Please note that the display below is just an estimated layout. Use the "Send Test" button to get an accurate preview.</div>
          <collective-goal-preview
            v-if="templateID === 'teams_collective_goal'"
            class="mb-3"
            :fromEmail="form.from"
            :job="inputForm.job || null"
            :contentShare="inputForm.contentShare || null"
            :collectiveGoal="collectiveGoal"
            :inputs="inputForm"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
          <promote-content-preview
            v-else-if="['teams_promote_a_job', 'teams_promote_an_internal_job', 'teams_promote_content'].includes(templateID)"
            class="mb-3"
            :fromEmail="form.from"
            :job="inputForm.job || null"
            :contentShare="inputForm.contentShare || null"
            :collectiveGoal="collectiveGoal"
            :inputs="inputForm"
            :isInternal="templateID === 'teams_promote_an_internal_job'"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
          <promote-multiple-content-preview
            v-else-if="['teams_promote_jobs', 'teams_promote_internal_jobs', 'teams_promote_content_shares'].includes(templateID)"
            class="mb-3"
            :fromEmail="form.from"
            :jobs="inputForm.jobs || null"
            :contentShares="inputForm.contentShares || null"
            :inputs="inputForm"
            :isInternal="templateID === 'teams_promote_internal_jobs'"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
          <schedule-multiple-content-preview
            v-else-if="templateID === 'teams_schedule_jobs' || templateID === 'teams_schedule_content_shares'"
            class="mb-3"
            :fromEmail="form.from"
            :jobs="inputForm.jobs || null"
            :contentShares="inputForm.contentShares || null"
            :inputs="inputForm"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
          <leaderboard-preview
            v-else-if="templateID === 'teams_leaderboard_individual' || templateID === 'teams_leaderboard_overall'"
            class="mb-3"
            :fromEmail="form.from"
            :job="inputForm.job || null"
            :contentShare="inputForm.contentShare || null"
            :isIndividual="templateID === 'teams_leaderboard_individual'"
            :inputs="inputForm"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
          <gamification-preview
            v-else-if="templateID === 'teams_gamification_individual'"
            class="mb-3"
            :fromEmail="form.from"
            :collectiveGoal="collectiveGoal"
            :job="inputForm.job || null"
            :contentShare="inputForm.contentShare || null"
            :inputs="inputForm"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
          <invite-preview
            v-else-if="templateID === 'teams_invite'"
            class="mb-3"
            :fromEmail="form.from"
            :collectiveGoal="collectiveGoal"
            :job="inputForm.job || null"
            :contentShare="inputForm.contentShare || null"
            :inputs="inputForm"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
          <content-preview
            v-else
            class="mb-3"
            :fromEmail="form.from"
            :job="inputForm.job || null"
            :contentShare="inputForm.contentShare || null"
            :jobs="inputForm.jobs || null"
            :contentShares="inputForm.contentShares || null"
            :collectiveGoal="collectiveGoal"
            :inputs="inputForm"
            :body="body"
            @sendTestMessage="$bvModal.show('send-test-message-modal')"
          />
        </b-col>
        <b-col cols="12" lg="5">
          <advanced-form
            autocomplete="off"
            :hideFormControl="true"
            :form="inputFormFields"
            v-bind="inputForm"
            v-model="inputForm"
          />
        </b-col>
      </b-row>

      <!-- Variables that can be used -->
      <b-row>
        <b-col>
          <b-card no-body class="mb-1">
            <b-card-header class="d-flex justify-content-between align-items-center bg-white">
              <div @click="displayVariables = !displayVariables" v-b-toggle.available-variables-card>
                <strong>Template variables you can use</strong>
              </div>
              <b-button @click="displayVariables = !displayVariables"  v-b-toggle.available-variables-card variant="outline-primary" class="px-2" size="sm">
                <font-awesome-icon v-if="displayVariables" icon="chevron-up" size="sm"/>
                <font-awesome-icon v-else icon="chevron-down" size="sm"/>
              </b-button>
            </b-card-header>
            <b-collapse id="available-variables-card" accordion="available-variables-card" role="tabpanel" class="p-3">
              <b>User related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'USER')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
              <b>Job related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'JOB')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
              <b>Leaderboard related variables</b> you can use:<br>
              <span v-for="{ key, description } in variables.filter(({ type }) => type === 'LEADERBOARD')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            </b-collapse>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col class="pt-3">
          <b-button
            variant="primary"
            @click="input"
            class="float-right"
            :disabled="isScheduled"
          >
            Save
          </b-button>
        </b-col>
      </b-row>

      <!-- Test Email Form -->
      <b-modal id="send-test-message-modal" title="Send Test Message" :hide-footer="true">
        <b-row>
          <b-col>
            <b-form>
              <!-- Email Addresses Field -->
              <test-message-form
                channel="teams"
                :body="body"
                :from="form.from"
                :job="inputForm.job"
                :channelID="form.channel"
                :contentShare="inputForm.contentShare"
                :jobs="inputForm.jobs"
                :contentShares="inputForm.contentShares"
                :collectiveGoal="collectiveGoal"
                :isIndividual="isIndividual"
              />
            </b-form>
          </b-col>
        </b-row>
      </b-modal>

      <!-- ALERT MODALS -->
      <b-modal id="target-group-change" title="Target group has changed" ok-only ok-title="I Understand">
        <div v-if="isIndividual">
          <p>This Microsoft Teams message will be sent as a direct message to everyone in the selected group.</p>
        </div>
        <div v-else>
          <p>This Microsoft Teams message will be sent as a group message to everyone in the selected group.</p>
        </div>
      </b-modal>
    </template>
  </div>

</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import teamsTemplates from '@src/views/campaign-admin/templates/teams';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import Share from '@src/components/Share';
import gridMixin from '@src/mixins/grid';
import ContentPreview from '@src/components/campaign-admin/message-previews/teams/content';
import CollectiveGoalPreview from '@src/components/campaign-admin/message-previews/teams/collectiveGoal';
import PromoteContentPreview from '@src/components/campaign-admin/message-previews/teams/promoteContent';
import InvitePreview from '@src/components/campaign-admin/message-previews/teams/invite';
import PromoteMultipleContentPreview from '@src/components/campaign-admin/message-previews/teams/promoteMultipleContent';
import ScheduleMultipleContentPreview from '@src/components/campaign-admin/message-previews/teams/scheduleMultipleContent';
import LeaderboardPreview from '@src/components/campaign-admin/message-previews/teams/leaderboard';
import GamificationPreview from '@src/components/campaign-admin/message-previews/teams/gamification';
import TestMessageForm from '@src/components/campaign-admin/test-message-form.vue';
import { mapRoot } from '@src/mixins/root';
import gql from 'graphql-tag';

const TeamsChannelsQuery = gql`
  query TeamsChannelsQuery {
    user {
      id
      teamsChannels {
        groups {
          id
          title
        }
        teams {
          id
          title
          channels {
            id
            title
          }
        }
      }
    }
  }
`;

export default {
  props: {
    campaignID: {
      type: String,
    },
    collectiveGoal: {
      type: Object,
    },
    value: {
      type: Object,
    },
    syncedEmailAddresses: {
      type: Array,
    }
  },
  components: {
    AdvancedForm,
    Share,
    TestMessageForm,
    ContentPreview,
    CollectiveGoalPreview,
    PromoteContentPreview,
    PromoteMultipleContentPreview,
    ScheduleMultipleContentPreview,
    InvitePreview,
    LeaderboardPreview,
    GamificationPreview,
    LoadingSpinner
  },
  apollo: {
    channels: {
      query: TeamsChannelsQuery,
      update(data) {
        const { user } = data || {};
        const { teamsChannels } = user || {};
        // const { teams, groups } = teamsChannels || {};
        const { teams } = teamsChannels || {};
        const channels = {
          // ...(Array.isArray(groups) ? groups.reduce((r, group) => ({ ...r, [group.id]: group.title }), {}) : {}),
          ...(Array.isArray(teams) ? teams.reduce((r, team) => {
            const { channels, title, id: teamID } = team || {};
            return {
              ...r,
              ...(Array.isArray(channels) ? channels.reduce((result, channel) => ({ ...result, [`${teamID}||${channel.id}`]: `${title} - ${channel.title}` }), {}) : {})
            };
          }, {}) : {})
        };
        console.log({ channels, data });
        return channels;
      },
      skip() {
        const { form } = this;
        if (form && form.to === 'CHANNEL') {
          console.log('START QUERY');
          return false;
        }
        return true;
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load Teams channels' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  data() {
    const { teamsMessage } = this.value || {};
    console.log({ teamsMessage });
    delete teamsMessage.__typename;
    const { template, inputs, body, job, contentShare, jobs, contentShares, ...rest } = teamsMessage || {};
    console.log({ inputs });
    console.log({ teamsMessage });
    return {
      form: {
        from: null,
        to: 'ALL_USERS',
        channel: null,
        sendAt: '',
        status: 'DRAFT',
        ...(rest || {})
      },
      templateID: template || null,
      isScheduled: false,
      inputForm: {
        ...Object.keys(inputs || {}).reduce((acc, key) => {
          if (key.includes('IMAGE')) {
            try {
              acc[key] = JSON.parse(inputs[key]);
            } catch (e) {
              console.log(e);
              acc[key] = inputs[key];
            }
          } else {
            acc[key] = inputs[key];
          }
          return acc;
        }, {}),
        ...(job ? { job } : {}),
        ...(contentShare ? { contentShare } : {}),
        ...(jobs ? { jobs } : {}),
        ...(contentShares ? { contentShares } : {})
      },
      displayVariables: false,
    };
  },
  mounted() {
    setInterval(() => {
      const { form } = this;
      const isScheduled = [ 'SCHEDULED', 'SCHEDULING', 'ONHOLD', 'DELIVERED', 'ERRORED' ].includes(form.status);
      const now = new Date();
      const sendAt = typeof form.sendAt === 'string' || typeof form.sendAt === 'number' ? new Date(form.sendAt) : form.sendAt;
      const isLessThanFiveMinutes = sendAt && (sendAt.getTime() - now.getTime()) < 300000 && form.status === 'READY';
      this.isScheduled = isScheduled || isLessThanFiveMinutes;
    }, 1000);
  },
  mixins: [
    gridMixin,
  ],
  watch: {
    isIndividual() {
      this.$bvModal.show('target-group-change');
    },
  },
  computed: {
    ...mapRoot([ 'isLNW', 'isApplusIdiada' ]),
    template() {
      const { templateID, isLNW, isApplusIdiada } = this;
      console.log({ templateID, isLNW, isApplusIdiada });
      let templateFolder = null;
      if (isLNW) {
        templateFolder = 'lnw';
      }
      if (isApplusIdiada) {
        templateFolder = 'applus';
      }
      return teamsTemplates(templateFolder).find(({ id }) => id === templateID);
    },
    toChoices() {
      const { isIndividual } = this;
      return {
        ALL_USERS: `All Participants: Includes everyone in the campaign - ${isIndividual ? 'sent as a direct Teams message' : 'posted in a group'}`,
        REGISTERED_USERS: `Active Participants: Includes those who have engaged with the campaign (e.g., signed up or shared content) - ${isIndividual ? 'sent as a direct Teams message' : 'posted in a group'}`,
        INVITED_USERS: `Inactive Participants: Includes those who have not engaged with the campaign - ${isIndividual ? 'sent as a direct Teams message' : 'posted in a group'}`,
        CHANNEL: 'Channel: Sent as a channel message',
      };
    },
    formFields() {
      const { syncedEmailAddresses, campaignID } = this;
      console.log({ campaignID });
      const defaultEmailOption = { NONE: '---- Select an email address ----' };
      return {
        from: {
          type: 'SELECT',
          label: 'From',
          disabled: !Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length || this.isScheduled,
          choices: Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length
            ? syncedEmailAddresses.reduce((out, { emailAddress, fullName }) => {
              out[emailAddress] = `${emailAddress} (${fullName})`;
              return out;
            }, defaultEmailOption) : [ defaultEmailOption ],
          helpText: 'The person on whose behalf this message will be delivered i.e. the sender of the message'
        },
        to: {
          label: 'To',
          type: 'SELECT',
          required: true,
          choices: this.toChoices,
          disabled: this.isScheduled,
          helpText: 'The target group of this Microsoft Teams message'
        },
        ...(this.form.to === 'CHANNEL' && this.channels ? {
          channel: {
            label: 'Channel',
            type: 'SELECT',
            required: true,
            disabled: this.isScheduled,
            choices: this.channels,
            helpText: 'The target group of this Microsoft Teams message'
          },
        } : {}),
        sendAt: {
          label: 'Send At',
          placeholder: 'e.g. 2020-12-01 13:00',
          disabled: this.isScheduled,
          helpText: 'The time when this Microsoft Teams message will be sent, in your timezone',
          type: 'DATETIME',
        },
        status: {
          label: 'Status',
          type: 'SELECT',
          required: true,
          disabled: this.isScheduled,
          choices: {
            READY: 'Ready',
            DRAFT: 'Draft',
          },
        },
      };
    },
    inputFormFields() {
      const { template } = this;
      console.log({ template });
      const inputFields = template ? template.inputs : {};
      return {
        ...(this.isJob ? {
          job: {
            label: 'Job',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: {
                status: 'ACTIVE',
              },
              many: false,
              order: 'CREATED_DESC',
            },
            helpText: 'Select the job you want to include in the Microsoft Teams message',
          },
        } : {}),
        ...(this.areJobs ? {
          jobs: {
            label: 'Jobs',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: {
                status: 'ACTIVE',
              },
              many: true,
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 jobs you want to include in the Microsoft Teams message',
          },
        } : {}),
        ...(this.isContent ? {
          contentShare: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: false,
              where: {
                status: 'ACTIVE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select the piece of content you want to include in the Microsoft Teams message',
          },
        } : {}),
        ...(this.arePiecesOfContent ? {
          contentShares: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: true,
              where: {
                status: 'ACTIVE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 pieces of content you want to include in the Microsoft Teams message',
          },
        } : {}),
        ...inputFields,
      };
    },
    body() {
      const { template, inputForm } = this;
      if (!template) return null;
      const { body: rawBody } = template;
      const body = typeof rawBody === 'object' ? JSON.parse(JSON.stringify(rawBody)) : rawBody;
      if (body && typeof body === 'string') {
        let replacedBody = body;
        Object.keys(inputForm).forEach(key => {
          if (typeof inputForm[key] === 'string') {
            replacedBody = replacedBody.replaceAll(`[[${key}]]`, inputForm[key]);
          }
        });
        return replacedBody;
      };
      const { jobs, contentShares } = inputForm;
      if (body && body.body && body.body.content && Array.isArray(body.attachments) && template.blocks && (Array.isArray(jobs) || Array.isArray(contentShares))) {
        // it only supoprts one type of blocks
        const [ blockKey ] = Object.keys(template.blocks);
        let items = [];
        if (blockKey === 'BLOCK_JOB') {
          const { jobs } = inputForm;
          items = jobs;
        }
        if (blockKey === 'BLOCK_CONTENT') {
          const { contentShares } = inputForm;
          items = contentShares;
        }
        console.log({ items });
        // create attachments
        const attachments = items.map(({ id }) => JSON.parse(JSON.stringify(template.attachment(id)).replaceAll('[ID]', id)));
        body.attachments = attachments;

        // update content
        const attachmentBlocks = items.map(({ id }) => template.blocks[blockKey](id)).join('');
        body.body.content = rawBody.body.content.replaceAll(`<${blockKey}>`, attachmentBlocks);
      }
      let replacedBody = JSON.stringify(body);
      Object.keys(inputForm).forEach(key => {
        // console.log({ key, value: inputForm[key], tunrDown: turndownService.turndown(inputForm[key]).replaceAll('\\', '') });
        console.log({ key, value: inputForm[key] });
        if (typeof inputForm[key] === 'string') {
          replacedBody = replacedBody.replaceAll(`[[${key}]]`, inputForm[key].replace(/\s/g, ' '));
        }
      });
      console.log({ replacedBody: JSON.parse(replacedBody) });
      // console.log({ replacedBody });
      return replacedBody;
    },
    usedVariables() {
      const { body } = this;
      const stringBody = typeof body === 'string' ? body : (body ? JSON.stringify(body) : null);
      return [ ...new Set((stringBody || '').split('{{').reduce((r, el) => {
        if (!el.includes('}}')) return r;
        const [ variable ] = el.split('}}');
        r.push(variable);
        return r;
      }, [])) ];
    },
    variables() {
      return [
        {
          key: 'JOB_TITLE',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Title of the selected job.',
        }, {
          key: 'JOB_SHARE_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'The link of the job users can share. This is generated uniquely for each user.',
        }, {
          key: 'JOB_DESCRIPTION',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected job.',
        }, {
          key: 'JOB_SHARE_TEXT',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the job.',
        }, {
          key: 'JOB_MATCH_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'LinkedIn network page of the user with possible matches',
        }, {
          key: 'CONTENT_TITLE',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Title of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_LINK',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'The link of the piece of content users can share. This is generated uniquely for each user.',
        }, {
          key: 'CONTENT_DESCRIPTION',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_TEXT',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the piece of content.',
        }, {
          key: 'LEADERBOARD_FIRST_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the first prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the second prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the third prize of the leaderboard.',
        }, {
          key: 'FIRST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The first name of the current user on the leaderboard.',
        }, {
          key: 'LAST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The last name of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POSITION',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The position of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POINTS',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The amount of points/tokens the current user has collected.',
        },
      ];
    },
    isIndividual() {
      const keys = this.variables.filter(({ target }) => target === 'INDIVIDUAL').map(({ key }) => key);
      const { relations } = this.template || {};
      return this.templateID === 'teams_invite' || !!keys.filter(key => this.usedVariables.includes(key)).length || (Array.isArray(relations) && (relations.includes('MANY_JOBS') || relations.includes('MANY_CONTENT')));
    },
    isJob() {
      const keys = this.variables.filter(({ type }) => type === 'JOB').map(({ key }) => key);
      const { relations } = this.template || {};
      return (Array.isArray(relations) && relations.includes('JOB')) || !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    isContent() {
      const keys = this.variables.filter(({ type }) => type === 'CONTENT').map(({ key }) => key);
      const { relations } = this.template || {};
      return (Array.isArray(relations) && relations.includes('CONTENT')) || !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    areJobs() {
      const { relations } = this.template || {};
      return Array.isArray(relations) && relations.includes('MANY_JOBS');
    },
    arePiecesOfContent() {
      const { relations } = this.template || {};
      return Array.isArray(relations) && relations.includes('MANY_CONTENT');
    }
  },
  methods: {
    input() {
      const { form, inputForm } = this;
      if (inputForm.job) {
        delete inputForm.job.customFields;
        delete inputForm.job.location;
        delete inputForm.job.createdAt;
        delete inputForm.job.status;
        delete inputForm.job.department;
        delete inputForm.job.__typename;
      }
      if (Array.isArray(inputForm.jobs)) {
        for (let i = 0; i < inputForm.jobs.length; i++) {
          delete inputForm.jobs[i].customFields;
          delete inputForm.jobs[i].location;
          delete inputForm.jobs[i].createdAt;
          delete inputForm.jobs[i].status;
          delete inputForm.jobs[i].department;
          delete inputForm.jobs[i].__typename;
        }
      }
      if (inputForm.contentShare) {
        delete inputForm.contentShare.createdAt;
        delete inputForm.contentShare.status;
        delete inputForm.contentShare.atsID;
        delete inputForm.contentShare.url;
        delete inputForm.contentShare.domain;
        delete inputForm.contentShare.imageURL;
        delete inputForm.contentShare.image;
        delete inputForm.contentShare.__typename;
      }
      if (Array.isArray(inputForm.contentShares)) {
        for (let y = 0; y < inputForm.contentShares.length; y++) {
          delete inputForm.contentShares[y].createdAt;
          delete inputForm.contentShares[y].status;
          delete inputForm.contentShares[y].atsID;
          delete inputForm.contentShares[y].url;
          delete inputForm.contentShares[y].domain;
          delete inputForm.contentShares[y].imageURL;
          delete inputForm.contentShares[y].image;
          delete inputForm.contentShares[y].__typename;
        }
      }
      const { job, contentShare, jobs, contentShares, ...rest } = inputForm;
      this.$emit('input', {
        teamsMessage: {
          template: this.templateID,
          ...form,
          ...(job ? { job } : {}),
          ...(contentShare ? { contentShare } : {}),
          ...(Array.isArray(jobs) ? { jobs } : {}),
          ...(Array.isArray(contentShares) ? { contentShares } : {}),
          target: this.isIndividual ? 'INDIVIDUAL' : 'GROUP',
          replacedBody: this.body,
          inputs: {
            ...(Object.keys(rest).reduce((acc, key) => {
              if (rest[key] && typeof rest[key] === 'object') {
                acc[key] = JSON.stringify(rest[key]);
              } else {
                acc[key] = rest[key];
              }
              return acc;
            }, {})),
          }
        } });
    },
  }
};
</script>


// WEBPACK FOOTER //
// src/components/campaign-admin/update-teams-message.vue